import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';

import {
  templates,
  type TemplateContext,
  type TemplateVariable,
} from '@/shared/api/resources';
import { Textarea } from '@/shared/ui/Textarea';

/**
 * Corpo do modelo com a paleta de variaveis.
 *
 * A variavel entra no ponto onde o cursor esta, e nao no fim do texto. Anexar ao fim
 * obriga quem escreve a recortar e colar a marcacao ate o lugar certo, e em um
 * contrato de tres paginas esse lugar quase nunca e o fim.
 *
 * O vocabulario vem do servidor, no contexto do tipo do modelo. Uma lista repetida
 * aqui ficaria desatualizada em silencio, e a divergencia so apareceria na
 * publicacao, que recusa marcacao desconhecida ou de outro contexto.
 */
export interface EditorDeCorpoProps {
  label: string;
  value: string;
  onChange: (valor: string) => void;
  contexto: TemplateContext;
  rows?: number;
  hint?: string | undefined;
  disabled?: boolean | undefined;
}

export function EditorDeCorpo({
  label,
  value,
  onChange,
  contexto,
  rows = 10,
  hint,
  disabled,
}: EditorDeCorpoProps) {
  const campo = useRef<HTMLTextAreaElement>(null);

  function inserir(marcacao: string) {
    const area = campo.current;

    // Sem o campo montado, ou sem foco, o cursor nao tem posicao conhecida. Anexar ao
    // fim e o comportamento previsivel nesse caso, e nao um palpite sobre onde a
    // pessoa queria.
    if (!area) {
      onChange(value + marcacao);
      return;
    }

    const inicio = area.selectionStart ?? value.length;
    const fim = area.selectionEnd ?? value.length;
    const novo = value.slice(0, inicio) + marcacao + value.slice(fim);
    onChange(novo);

    // O cursor vai para depois da marcacao inserida, que e onde quem escreve continua
    // digitando. Deixa-lo antes obrigaria a mover a mao para o teclado de setas a
    // cada variavel.
    //
    // A posicao e ajustada depois da renderizacao: antes dela o campo ainda mostra o
    // texto antigo, e mover o cursor ali nao teria efeito.
    requestAnimationFrame(() => {
      const posicao = inicio + marcacao.length;
      area.focus();
      area.setSelectionRange(posicao, posicao);
    });
  }

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        ref={campo}
        label={label}
        rows={rows}
        value={value}
        disabled={disabled}
        className="font-mono text-xs"
        hint={hint}
        onChange={(evento) => onChange(evento.target.value)}
      />
      <PaletaDeVariaveis contexto={contexto} onEscolher={inserir} disabled={disabled} />
    </div>
  );
}

/** Nome de cada grupo na paleta, na lingua de quem monta o modelo. */
const nomeDoGrupo: Record<string, string> = {
  empresa: 'Empresa',
  data: 'Data',
  tomador: 'Tomador',
  segurado: 'Segurado',
  corretor: 'Corretor',
  seguradora: 'Seguradora',
  produto: 'Produto e ramo',
  modalidade: 'Modalidade',
  operacao: 'Operacao',
  apolice: 'Apolice',
  documento: 'Documento',
  premio: 'Demonstrativo do premio',
  parcela: 'Parcelas',
  cobertura: 'Coberturas',
  cobertura_adicional: 'Coberturas adicionais',
  condicoes: 'Condicoes',
  clausula_particular: 'Clausulas particulares',
  avalista: 'Avalistas',
  beneficiario: 'Beneficiarios',
  clausula: 'Clausula particular: marcadores',
  consorcio: 'Consorcio',
  objeto: 'Objeto da modalidade: marcadores',
  endosso: 'Endosso',
  laudo: 'Laudo de emissao',
  usuario: 'Usuario',
  pessoa: 'Pessoa',
  sistema: 'Sistema',
  documento_pendente: 'Documentos pendentes',
};

interface Grupo {
  nome: string;
  lista: string;
  variaveis: TemplateVariable[];
}

/** Agrupa as variaveis mantendo a ordem de apresentacao que o servidor devolveu. */
function agrupar(variaveis: TemplateVariable[]): Grupo[] {
  const grupos: Grupo[] = [];
  const porNome = new Map<string, Grupo>();
  for (const variavel of variaveis) {
    let grupo = porNome.get(variavel.group);
    if (!grupo) {
      grupo = { nome: variavel.group, lista: variavel.list, variaveis: [] };
      porNome.set(variavel.group, grupo);
      grupos.push(grupo);
    }
    grupo.variaveis.push(variavel);
  }
  return grupos;
}

/**
 * Paleta de variaveis do contexto, servida pelo servidor.
 *
 * A escolha entrega a marcacao pronta. A variavel de lista vem com o aviso de que so
 * vale numa linha de tabela ou num bloco repetido, e o bloco tem botao proprio, para
 * que ninguem precise lembrar a grafia de {{repetir.lista}} e {{fim.lista}}.
 */
export function PaletaDeVariaveis({
  contexto,
  onEscolher,
  disabled,
}: {
  contexto: TemplateContext;
  onEscolher: (marcacao: string) => void;
  disabled?: boolean | undefined;
}) {
  const [busca, setBusca] = useState('');
  const consulta = useQuery({
    queryKey: ['template-variables', contexto],
    queryFn: ({ signal }) => templates.variables(contexto, signal),
    staleTime: 5 * 60 * 1000,
  });

  const grupos = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const filtradas = (consulta.data ?? []).filter(
      (variavel) =>
        termo === '' ||
        variavel.name.includes(termo) ||
        variavel.description.toLowerCase().includes(termo),
    );
    return agrupar(filtradas);
  }, [consulta.data, busca]);

  if (consulta.isPending || consulta.isError) return null;

  // Com poucos grupos, ou durante a busca, tudo aberto. O vocabulario da apolice tem
  // dezenas de grupos, e abertos de uma vez eles empurram o corpo para fora da tela.
  const abertos = busca.trim() !== '' || grupos.length <= 4;

  return (
    <div className="flex flex-col gap-2">
      <input
        type="search"
        aria-label="Buscar variavel"
        placeholder="Buscar variavel"
        value={busca}
        onChange={(evento) => setBusca(evento.target.value)}
        className="h-8 rounded-md border border-line-strong bg-surface px-2 text-xs text-ink-800"
      />
      <div className="flex flex-col gap-1">
        {grupos.map((grupo) => (
          <details
            key={grupo.nome}
            open={abertos}
            className="rounded-md border border-line px-2 py-1"
          >
            <summary className="cursor-pointer text-2xs font-semibold text-ink-700">
              {nomeDoGrupo[grupo.nome] ?? grupo.nome} ({grupo.variaveis.length})
            </summary>
            {grupo.lista ? (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <p className="text-2xs text-ink-500">
                  Lista: use numa linha de tabela, que se repete por item, ou dentro do
                  bloco repetido.
                </p>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    onEscolher(`{{repetir.${grupo.lista}}}\n\n{{fim.${grupo.lista}}}`)
                  }
                  className="rounded-md border border-line-strong bg-surface px-2 py-0.5 font-mono text-2xs text-ink-700 hover:bg-ink-50 disabled:opacity-50"
                >
                  Inserir bloco repetido
                </button>
              </div>
            ) : null}
            <div className="mt-1 flex flex-wrap gap-1.5">
              {grupo.variaveis.map((variavel) => (
                <button
                  key={variavel.name}
                  type="button"
                  disabled={disabled}
                  title={descricaoCompleta(variavel)}
                  onClick={() => onEscolher(`{{${variavel.name}}}`)}
                  className="flex items-center gap-1 rounded-md border border-line-strong bg-surface px-2 py-1 font-mono text-2xs text-ink-700 transition-colors duration-(--duration-fast) hover:bg-ink-50 disabled:opacity-50"
                >
                  {`{{${variavel.name}}}`}
                  {variavel.rich ? <Marca texto="texto rico" /> : null}
                  {variavel.personal_data ? <Marca texto="dado pessoal" /> : null}
                  {variavel.policyholder_ccg_only ? (
                    <Marca texto="so na CCG do tomador" />
                  ) : variavel.available ? null : (
                    <Marca texto="sem dado ainda" />
                  )}
                </button>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

function Marca({ texto }: { texto: string }) {
  return (
    <span className="rounded bg-ink-100 px-1 font-sans text-2xs text-ink-600">
      {texto}
    </span>
  );
}

/** Descricao com o que a marca resumida nao diz. */
function descricaoCompleta(variavel: TemplateVariable): string {
  const partes = [variavel.description];
  if (variavel.rich) {
    partes.push(
      'Texto rico: sozinha no paragrafo, entra com paragrafos e negrito; no meio de uma frase, entra como texto corrido.',
    );
  }
  if (variavel.personal_data) {
    partes.push('Dado pessoal: use so quando o documento precisa dele.');
  }
  if (variavel.policyholder_ccg_only) {
    partes.push(
      'So a CCG do tomador preenche: a variavel vale so no modelo do tipo CCG do tomador.',
    );
  } else if (!variavel.available) {
    partes.push(
      'A plataforma ainda nao tem este dado: a variavel sai vazia ate o cadastro dela existir.',
    );
  }
  return partes.join(' ');
}
